import React, { useState } from "react";
import { Award, Briefcase, ChevronRight, HardHat, Check, ExternalLink } from "lucide-react";
import { TRACTION_DATA } from "../../../data";
import { CREDENTIALS_DATA } from "../../credential/credential.constants";

export default function AboutSection() {

  return (
    <section id="about" className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-3 gap-12 border-b border-brand-sand">
      {/* Narrative block */}
      <div className="lg:col-span-2 space-y-6 text-brand-espresso leading-relaxed text-base">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-burgundy font-bold">01. BUILT FOR HIGH-STAKES AI SYSTEMS</span>
          <div className="h-px bg-brand-sand grow" />
        </div>
        
        <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-charcoal tracking-tight leading-tight">
          Governed AI Systems & The Lifecycle Philosophy
        </h2>
        
        <p className="text-stone-700 font-semibold mb-3">
          I design deterministic AI memory and graph systems for environments where context, sequence, permissions, and compliance boundaries matter.
        </p>
        <p className="text-stone-700 font-light">
          Standard AI stacks — vector search, plain LLMs, and chatbot wrappers — start to fail when the correct answer depends on relationship logic, data lineage, and runtime constraints. At that point, the problem stops being prompt engineering and becomes a systems architecture problem.
        </p>
        <p className="text-stone-700 font-bold border-l-4 border-brand-orange pl-4 mt-6">
          I am not here to become a permanent line item. I am here to build a deterministic system and replace myself with your internal team.
        </p>
        <p className="text-stone-700 font-light mt-4">
          My work combines Neo4j graph systems, agent memory, GraphRAG, runtime validation, and zero-trust security patterns for teams operating in regulated or high-liability environments. Once the architecture is proven and the systems are deployed, I transition governance to your internal leaders.
        </p>

        {/* Credentials badge with franchise aesthetic - interactive trigger to open the certification console */}
        <a
          href="/credentials"
          className="w-full text-left p-5 bg-brand-sand/55 hover:bg-brand-sand/85 border-l-4 border-brand-burgundy hover:border-brand-orange rounded-r-md flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] group focus:outline-none block"
          title="Click to view full credential portfolio"
        >
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-sm bg-brand-burgundy group-hover:bg-brand-orange text-white flex items-center justify-center shrink-0 shadow transition-colors">
              <Award className="text-brand-orange group-hover:text-white w-6 h-6 transition-colors" />
            </div>
            <div>
              <h3 className="text-[10px] font-mono text-brand-burgundy group-hover:text-brand-orange uppercase tracking-wider font-bold flex items-center gap-1">
                Verified Graph &amp; AI Credentials <span className="text-stone-700 font-medium font-sans">(Click to view 40+ certs)</span>
              </h3>
              <p className="text-sm font-bold text-brand-charcoal font-display flex items-center gap-1.5 flex-wrap">
                Neo4j Certified Professional · Graph Data Science · Agent Memory · Knowledge Graphs with LLMs
                <ExternalLink className="w-3.5 h-3.5 text-stone-600 group-hover:text-brand-orange shrink-0" />
              </p>
            </div>
          </div>
          <div className="text-xs font-mono text-brand-burgundy bg-white border border-brand-sand px-3 py-1.5 rounded font-bold shadow-sm self-start sm:self-auto">
            CREDENTIAL: 7492-N4J
          </div>
        </a>

        {/* Construction roots section styled beautifully like wooden planks or a textured paper block */}
        <div className="pt-6 border-t border-brand-sand mt-8">
          <div className="flex items-center gap-2.5 text-brand-burgundy font-bold text-xl font-display mb-3">
            <HardHat className="text-brand-orange w-6 h-6" /> Grounded Roots, Concrete Results
          </div>
          <p className="text-stone-700 font-light mb-4">
            Before I designed graph architectures, my roots were on the concrete. I funded my early career working construction sites, stamping plants, foundries, and high-pressure production scheduling environments where a bad dependency, an idle crew, or a data-integrity failure costs real money.
          </p>
          <p className="text-stone-700 font-light">
            That operating instinct still shapes my AI work: find the real constraint, map the dependency, and design around the failure mode before it becomes expensive.
          </p>
        </div>

        {/* Verified Depth section */}
        <div className="pt-6 border-t border-brand-sand mt-8 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-sand border border-brand-sand rounded-sm font-mono text-[9px] text-brand-burgundy font-bold uppercase tracking-widest">
              ✦ Evidence of Judgment
            </div>
            <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-burgundy/10 border border-brand-burgundy/20 rounded-sm font-mono text-[9px] text-brand-burgundy font-bold uppercase tracking-widest">
              10th Anniversary (2016–2026) · AI Systems Since 2016
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-brand-charcoal font-display">
            A Decade of Verifiable Technical Execution
          </h3>
          <p className="text-stone-700 font-light text-sm sm:text-base leading-relaxed">
            Technical authority is earned through production scars, not just certifications. My advisory practice is backed by a decade of enterprise deployments (2016–2026), beginning with IBM Watson and evolving through graph data science, agent memory architectures, and frontier models like Gemini Enterprise. 
          </p>
          <p className="text-stone-700 font-light text-sm sm:text-base leading-relaxed">
            The foundation of this judgment goes back further to the mid-1990s, when I co-founded Telcare and served as the chief architect for a national IVR-based telehealth network built alongside IBM, AT&T/Bell Labs, and Oracle. 
          </p>
          <p className="text-stone-700 font-light text-sm sm:text-base leading-relaxed">
            I don't just understand the hype cycle; I know what breaks in production. This history translates into a practical architecture discipline that preserves context, enforces boundaries, and explains decisions under pressure.
          </p>
          <div className="pt-2">
            <a
              href="/credentials"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-burgundy hover:bg-brand-burgundy/90 text-white rounded-sm text-xs font-mono font-bold uppercase tracking-wider shadow-sm hover:shadow transition-all cursor-pointer"
            >
              View Full Credential History <ChevronRight className="w-3.5 h-3.5 text-brand-orange" />
            </a>
          </div>
        </div>
      </div>

      {/* Sidebar Representative Engagements - dark charcoal background card */}
      <div className="bg-brand-charcoal text-brand-sand rounded p-6 flex flex-col justify-between relative border-t-4 border-brand-orange shadow-lg">
        <div>
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-brand-espresso">
            <h3 className="text-xs font-mono uppercase tracking-widest text-brand-sand/60 font-bold">Representative Engagements</h3>
            <Briefcase className="text-brand-orange w-4 h-4" />
          </div>
          
          <div className="space-y-5">
            {TRACTION_DATA.map((item) => (
              <div 
                key={item.name}
                className="p-4 rounded-sm transition-all duration-300 relative bg-brand-espresso/45 hover:bg-brand-espresso border-l-4 border-brand-burgundy hover:border-brand-orange hover:shadow-md group"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-white font-bold text-sm tracking-tight font-display">{item.name}</div>
                    {item.role && <div className="text-brand-orange font-mono text-[10px] mt-0.5 uppercase tracking-wider">{item.role}</div>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-brand-sand/40 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-brand-orange" />
                </div>
                <p className="text-brand-sand/70 text-xs mt-2.5 leading-relaxed font-light">
                  {item.description}
                </p>
                {item.tag && (
                  <div className="text-[9px] font-mono text-white mt-3 bg-brand-burgundy px-2 py-0.5 rounded inline-block uppercase tracking-wider">
                    {item.tag}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4 mt-8">
          <div className="text-[10px] text-brand-sand/50 leading-relaxed font-mono">
            *Representative examples only. Full project details and engagement context are available in the portfolio.
          </div>
          <a 
            href="/portfolio"
            className="w-full py-2.5 px-4 bg-brand-burgundy hover:bg-brand-orange text-white text-xs font-mono font-bold uppercase tracking-widest rounded-sm shadow-sm hover:shadow transition-all flex items-center justify-center gap-1.5 group/btn cursor-pointer"
          >
            View Project Portfolio <ChevronRight className="w-4 h-4 text-brand-orange transition-transform group-hover/btn:translate-x-1" />
          </a>
        </div>
      </div>


    </section>
  );
}
